import React from 'react';
import Grid from '@mui/material/Grid';
import SingleAccordion from './SingleAccordion';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import styled from '@mui/system/styled';
import ModalUnstyled from '@mui/base/ModalUnstyled';
import { CloseIcon } from '../icons';
import theme from '@/mui/theme';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { idFromString } from '@/utils/miscellaneous';

interface FilterMenuProps {
  accordions: {
    accordionTitle: string
    accordionCheckbox?: [
      {
        label: string
        value: string
      }
    ]
    accordionSlider?: {
      valueMin?: number
      valueMax?: number
      label?: string
      marks?: [
        {
          value: any,
          label: string
        }
      ]
      marksValues: any[]
      isReset: boolean | undefined
    }
  }[]
  facets: any;
  dataModal: {
    btnOpenModal?: string
    title?: string
    backButton?: string
    confirmButton?: string
  }
  storeData: {
    storeId: string
    langId: string
    catalogId: string
    locale: string
    categoryId: string,
    orderBy: string,
    pageNumber: string,
    pageSize: string
  }
  handleFilter: Function
}

const StyledModal = styled(ModalUnstyled)({
  position: 'absolute',
  top: '0',
  maxWidth: '100%',
  bottom: 'unset',
  zIndex: '1300',
  right: '0',
  left: '0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&.MuiModal-hidden': {
    display: 'none',
  }
})

const Backdrop = styled('div')`
  z-index: -1;
  position: fixed;
  right: 0;
  bottom: 0;
  top: 0;
  left: 0;
  background-color: #000000;
  opacity: 0.5;
  -webkit-tap-highlight-color: transparent;
`;

const StyledBox = styled(Box)({
  position: 'relative',
  backgroundColor: '#ffffff',
  border: '1px solid #979797',
  padding: '0 16px 115px',
  margin: '0',
  maxWidth: '100%',
  width: '100%',
  minHeight: '100vh',
})

const StyledCloseIcon = styled(CloseIcon)({
  position: 'absolute',
  top: '2px',
  right: '12px',
  width: '24px',
  height: '24px',
  cursor: 'pointer',
  padding: '20px',
  boxSizing: 'content-box',
  zIndex: '99',
})

const StyledTitle = styled(Typography)({
  color: "#182641",
  fontSize: "24px",
  fontWeight: "bold",
  letterSpacing: "-0.1px",
  lineHeight: "30px",
  marginBottom: "30px",
  textAlign: 'center',
})

const StyledBoxButton = styled(Box)({
  position: 'fixed',
  zIndex: 1000,
  bottom: '0',
  left: '0',
  width: '100%',
  padding: '26px 0',
  borderTop: '1px solid #979797',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#fff',
})

const StyledButton = styled(Button)({
  backgroundColor: '#ff5c04',
  borderRadius: '5px',
  padding: '16px 40px',
  fontSize: '14px',
  fontWeight: '600',
  lineHeight: 'normal',
  letterSpacing: '0',
  color: '#ffffff'
})

const SecondaryStyledButton = styled(Button)({
  backgroundColor: '#ffffff',
  border: '1px solid #e6700d',
  color: '#ff5c04',
  borderRadius: '5px',
  padding: '15px 29px',
  flex: '1',
  marginRight: '16px',
  fontSize: '14px',
  fontWeight: '600',
  lineHeight: 'normal',
  letterSpacing: '0',
  '+ button': {
    padding: '16px 30px',
    flex: '1'
  }
})

const ButtonBox = styled(Box)({
  position: 'fixed',
  backgroundColor: '#ffffff',
  bottom: '0',
  left: '0',
  width: '100%',
  padding: '26px 30px',
  borderTop: '1px solid #979797',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
})

const FilterMenu: React.FC<FilterMenuProps> = ({ accordions, facets, dataModal, storeData, handleFilter }) => {
  const matches = useMediaQuery(theme.breakpoints.up('md'));
  const [openModal, setOpenModal] = React.useState(false);

  const handleOpen = () => {
    document.querySelector('main')!.classList.add('modal-open');
    setOpenModal(true);
  }

  const handleClose = () => {
    document.querySelector('main')!.classList.remove('modal-open');
    setOpenModal(false);
  }

  if (matches) {
    return <>
      {accordions && accordions.length > 0 && accordions.map((accordionData, index) => <SingleAccordion key={idFromString(accordionData.accordionTitle)} index={index} accordionData={accordionData} facets={facets} storeData={storeData} handleFilter={handleFilter} />)}
    </>
  } else {
    return <>
      <StyledBoxButton>
        <StyledButton onClick={handleOpen}>{dataModal.btnOpenModal}</StyledButton>
      </StyledBoxButton>
      {openModal &&
        <StyledModal
          aria-labelledby="unstyled-modal-title"
          aria-describedby="unstyled-modal-description"
          open={openModal}
          onClose={handleClose}
          components={{
            Backdrop: Backdrop
          }}
          disableScrollLock={true}
        >
          <StyledBox>
            <StyledCloseIcon onClick={handleClose} />
            <Container>
              <Grid container rowSpacing={0} sx={{ position: 'relative' }}>
                {dataModal.title &&
                  <Grid item xs={12} sx={{ marginTop: { xs: '18px' } }}>
                    <StyledTitle>{dataModal.title}</StyledTitle>
                  </Grid>
                }
                <Grid item xs={12}>
                  {accordions && accordions.length > 0 && accordions.map((accordionData, index) => {
                    return <SingleAccordion key={idFromString(accordionData.accordionTitle)} index={index} accordionData={accordionData} facets={facets} storeData={storeData} handleFilter={handleFilter} />;
                  })}
                </Grid>
              </Grid>
            </Container>
            <ButtonBox>
              {dataModal.backButton &&
                <SecondaryStyledButton onClick={handleClose}>{dataModal.backButton}</SecondaryStyledButton>
              }
              {dataModal.confirmButton &&
                <StyledButton onClick={handleClose}>{dataModal.confirmButton}</StyledButton>
              }
            </ButtonBox>
          </StyledBox>
        </StyledModal>
      }
    </>
  }
}

export default FilterMenu;
